import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { loadFromJSON } from "@excalidraw/excalidraw/data/json";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import { hashElementsVersion, hashString } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import { isCloudEnabled, isSharedKey } from "./cloud/config";
import { CloudProvider, useCloud } from "./cloud/context";
import { LoginScreen } from "./components/cloud/LoginScreen";
import { SaveToCloudDialog } from "./components/cloud/SaveToCloudDialog";
import { OpenFromCloudDialog } from "./components/cloud/OpenFromCloudDialog";
import { saveFile } from "./cloud/storage";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

// Cloud autosave timing. Trailing debounce after the last edit, with a
// max-wait ceiling so continuous drawing still saves periodically.
const AUTOSAVE_DEBOUNCE_MS = 2_000;
const AUTOSAVE_MAX_WAIT_MS = 10_000;

// Content signature that ignores volatile fields. Excalidraw bumps
// version/versionNonce/updated on history capture even when the visible scene
// is unchanged; hashing those would cause a redundant second save per edit.
// Hashing the rest persists only on real content changes.
const sceneContentSignature = (
  elements: readonly OrderedExcalidrawElement[],
): number =>
  hashString(
    JSON.stringify(
      elements.map(({ version, versionNonce, updated, ...rest }) => rest),
    ),
  );

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // A new non-null fileHandle means a local disk file was just opened
    // (Chromium: menu/Ctrl+O/drag/paste/PWA). Detach so autosave doesn't
    // overwrite the bound cloud file. Act only on the transition.
    const handle = appState.fileHandle ?? null;
    if (handle !== lastFileHandleRef.current) {
      const openedLocalFile = handle !== null;
      lastFileHandleRef.current = handle;
      if (openedLocalFile) {
        detachCloudFile();
      }
    }

    triggerAutoSave();

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  // ---------------------------------------------------------------------------
  // Cloud integration
  // ---------------------------------------------------------------------------
  const {
    user: cloudUser,
    currentKey: cloudCurrentKey,
    setCurrentKey: setCloudCurrentKey,
    setCurrentFilename: setCloudCurrentFilename,
    registerBindHandlers,
    saveDialogOpen,
    saveDialogPrefix,
    openSaveDialog,
    closeSaveDialog,
    openDialogOpen,
    openOpenDialog,
    closeOpenDialog,
  } = useCloud();

  const cloudEnabled = isCloudEnabled() && !!cloudUser;

  // ---------------------------------------------------------------------------
  // Auto-save
  //
  // Binding ref holds the key + ETag for the currently-loaded cloud file. It's
  // a ref (not state) so the debounced save callback always reads the live
  // value — eliminates a class of stale-closure bugs where edits to file B
  // would be written to file A's key after a fast file switch.
  //
  // Epoch counter is bumped on every bind/unbind. A pending save captures the
  // epoch at schedule time and re-checks before committing the result — any
  // save that completes after a file swap is discarded.
  //
  // ETag is used as If-Match on shared/ keys only (personal files are
  // IAM-isolated, no concurrent writers possible). On 412 PreconditionFailed
  // we halt autosave for the file and tell the user to reopen.
  // ---------------------------------------------------------------------------
  type Binding = { key: string; etag: string | null; epoch: number };
  type AutoSaveStatus =
    | { kind: "idle" }
    | { kind: "pending" }
    | { kind: "saving" }
    | { kind: "saved"; at: number }
    | { kind: "error"; message: string }
    | { kind: "conflict" };

  const currentBindingRef = useRef<Binding | null>(null);
  const epochRef = useRef(0);
  const pendingSavePromiseRef = useRef<Promise<void> | null>(null);
  const suppressNextAutosaveRef = useRef(false);
  const isPendingRef = useRef(false);
  // Timestamp of the first edit in the current dirty cycle. Used to enforce a
  // max-wait so continuous drawing can't starve the trailing debounce forever.
  const firstQueuedAtRef = useRef<number | null>(null);
  // Hash of the elements last persisted to S3. onChange fires on many events
  // that don't change content (font loads, selection, viewport) — gating on
  // this hash stops those from spamming PUTs and from flipping the status
  // chip back to "Saving…" so it can settle on "Saved".
  const lastSavedSceneVersionRef = useRef<number | null>(null);
  // Content signature of what was last persisted — dedupes redundant PUTs when
  // only versionNonce churned (see sceneContentSignature).
  const lastSavedContentSigRef = useRef<number | null>(null);
  // Last seen appState.fileHandle — a transition to a new non-null handle means
  // a local disk file was opened, so we detach the cloud binding.
  const lastFileHandleRef = useRef<unknown>(null);
  const excalidrawAPIRef = useRef(excalidrawAPI);
  useEffect(() => {
    excalidrawAPIRef.current = excalidrawAPI;
  }, [excalidrawAPI]);

  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>({
    kind: "idle",
  });

  const flushAutoSave = useMemo(
    () =>
      debounce(() => {
        // This cycle is being serviced now — clear the max-wait marker so the
        // next edit starts a fresh cycle.
        firstQueuedAtRef.current = null;
        const binding = currentBindingRef.current;
        const api = excalidrawAPIRef.current;
        if (!binding || !api) {
          return;
        }
        const scheduledEpoch = binding.epoch;
        const shared = isSharedKey(binding.key);

        pendingSavePromiseRef.current = (async () => {
          try {
            const elements = [...api.getSceneElements()];
            const savedVersion = hashElementsVersion(elements);
            const contentSig = sceneContentSignature(elements);
            // versionNonce churn only (history capture) — nothing new to
            // persist. Absorb the version so the gate stops re-scheduling,
            // settle the chip, and skip the redundant PUT.
            if (contentSig === lastSavedContentSigRef.current) {
              lastSavedSceneVersionRef.current = savedVersion;
              isPendingRef.current = false;
              setAutoSaveStatus({ kind: "saved", at: Date.now() });
              return;
            }
            setAutoSaveStatus({ kind: "saving" });
            const { etag } = await saveFile(
              binding.key,
              {
                elements,
                appState: api.getAppState(),
                files: api.getFiles(),
              },
              shared && binding.etag ? { ifMatch: binding.etag } : undefined,
            );
            if (epochRef.current !== scheduledEpoch) {
              return;
            }
            currentBindingRef.current = { ...binding, etag };
            lastSavedSceneVersionRef.current = savedVersion;
            lastSavedContentSigRef.current = contentSig;
            isPendingRef.current = false;
            setAutoSaveStatus({ kind: "saved", at: Date.now() });
          } catch (err: any) {
            if (epochRef.current !== scheduledEpoch) {
              return;
            }
            const is412 =
              err?.name === "PreconditionFailed" ||
              err?.$metadata?.httpStatusCode === 412;
            if (is412) {
              setAutoSaveStatus({ kind: "conflict" });
              excalidrawAPIRef.current?.setToast({
                message:
                  "Cloud file changed remotely — reopen to merge. Autosave paused.",
                duration: 8000,
                closable: true,
              });
              currentBindingRef.current = null;
            } else {
              setAutoSaveStatus({
                kind: "error",
                message: err?.message ?? "Save failed",
              });
            }
          } finally {
            pendingSavePromiseRef.current = null;
          }
        })();
      }, AUTOSAVE_DEBOUNCE_MS),
    [],
  );

  const triggerAutoSave = useCallback(() => {
    if (!cloudEnabled || !currentBindingRef.current || !excalidrawAPI) {
      return;
    }
    // Hash the SAME set the save path persists — non-deleted elements. The
    // onChange `elements` arg includes deleted ones (App.tsx getElements-
    // IncludingDeleted), which would never match the saved baseline once
    // anything is deleted, pinning autosave permanently "on".
    const elements = excalidrawAPI.getSceneElements();
    const version = hashElementsVersion(elements);
    if (suppressNextAutosaveRef.current) {
      suppressNextAutosaveRef.current = false;
      // Record the just-loaded scene so the first real edit is what triggers
      // a save, not the updateScene that loaded the file.
      lastSavedSceneVersionRef.current = version;
      lastSavedContentSigRef.current = sceneContentSignature(elements);
      return;
    }
    // While the Save dialog is open the user is mid-decision on destination.
    // Don't autosave to the previous binding — we'd race the dialog's PUT.
    if (saveDialogOpen) {
      return;
    }
    // No content change since the last save — ignore. Stops PUT spam from
    // non-edit onChange events and lets the chip rest on "Saved".
    if (version === lastSavedSceneVersionRef.current) {
      return;
    }
    const now = Date.now();
    // Mark dirty so the chip flips to "Unsaved" — but only on the *first*
    // edit of a cycle. onChange fires many times per second during drag; a
    // setState per tick triggers a React re-render storm that visibly blanks
    // the canvas. Use a ref guard so we transition exactly once per cycle.
    if (!isPendingRef.current) {
      isPendingRef.current = true;
      firstQueuedAtRef.current = now;
      setAutoSaveStatus((prev) =>
        prev.kind === "saving" || prev.kind === "conflict"
          ? prev
          : { kind: "pending" },
      );
    }
    // Max-wait: a pure trailing debounce starves under continuous drawing —
    // onChange fires faster than the debounce interval, so the timer never
    // elapses. Force a flush once edits have been queued past the ceiling.
    if (
      firstQueuedAtRef.current !== null &&
      now - firstQueuedAtRef.current >= AUTOSAVE_MAX_WAIT_MS
    ) {
      flushAutoSave.flush();
      return;
    }
    flushAutoSave();
  }, [cloudEnabled, excalidrawAPI, flushAutoSave, saveDialogOpen]);

  const bindCloudFile = useCallback(
    async (
      key: string,
      etag: string | null,
      opts?: { flushPrevious?: boolean },
    ) => {
      // flushPrevious=true (default): used when *loading* a different file.
      // The in-memory scene still belongs to the previous binding, so we want
      // its pending autosave to land before we swap.
      //
      // flushPrevious=false: used when the user just *uploaded* the current
      // scene to a NEW key (Save As). The scene now belongs to the new key —
      // letting the pending debounce fire would write the current scene to
      // the OLD key (overwriting the previous file with the forked content).
      // Cancel the debounce instead. An in-flight PUT (one that already
      // started before this call) is allowed to complete to the old key; the
      // epoch bump just prevents it from updating our new binding.
      const flushPrev = opts?.flushPrevious ?? true;
      if (flushPrev) {
        flushAutoSave.flush();
        if (pendingSavePromiseRef.current) {
          try {
            await pendingSavePromiseRef.current;
          } catch {
            // Surfaced via autoSaveStatus already.
          }
        }
      } else {
        flushAutoSave.cancel();
      }
      epochRef.current += 1;
      currentBindingRef.current = { key, etag, epoch: epochRef.current };
      isPendingRef.current = false;
      firstQueuedAtRef.current = null;
      if (flushPrev) {
        // Open: updateScene runs right after this and fires a spurious
        // onChange with the freshly-loaded content. Eat that one; it will set
        // the saved-version baseline so the first real edit triggers a save.
        suppressNextAutosaveRef.current = true;
        lastSavedSceneVersionRef.current = null;
        lastSavedContentSigRef.current = null;
      } else {
        // Save As: the current scene was just uploaded to `key`, and no
        // updateScene follows. Baseline to the current scene now so the next
        // real edit (not this no-op) is what saves.
        suppressNextAutosaveRef.current = false;
        const api = excalidrawAPIRef.current;
        const els = api ? api.getSceneElements() : null;
        lastSavedSceneVersionRef.current = els
          ? hashElementsVersion(els)
          : null;
        lastSavedContentSigRef.current = els
          ? sceneContentSignature(els)
          : null;
      }
      setAutoSaveStatus({ kind: "saved", at: Date.now() });
    },
    [flushAutoSave],
  );

  const unbindCloudFile = useCallback(async () => {
    flushAutoSave.flush();
    if (pendingSavePromiseRef.current) {
      try {
        await pendingSavePromiseRef.current;
      } catch {
        // ignore
      }
    }
    epochRef.current += 1;
    currentBindingRef.current = null;
    isPendingRef.current = false;
    firstQueuedAtRef.current = null;
    lastSavedSceneVersionRef.current = null;
    lastSavedContentSigRef.current = null;
    setAutoSaveStatus({ kind: "idle" });
  }, [flushAutoSave]);

  // Detach from the bound cloud file (e.g. when a local disk file is opened).
  // Cancels — never flushes — the pending save, so the now-local scene is NOT
  // written to the cloud key. No-op when nothing is bound.
  const detachCloudFile = useCallback(() => {
    if (!currentBindingRef.current) {
      return;
    }
    flushAutoSave.cancel();
    epochRef.current += 1;
    currentBindingRef.current = null;
    isPendingRef.current = false;
    firstQueuedAtRef.current = null;
    suppressNextAutosaveRef.current = false;
    lastSavedSceneVersionRef.current = null;
    lastSavedContentSigRef.current = null;
    setCloudCurrentKey(null);
    setCloudCurrentFilename(null);
    setAutoSaveStatus({ kind: "idle" });
    excalidrawAPIRef.current?.setToast({
      message:
        "Opened a local file — cloud autosave off. Use Save to Cloud to store it.",
      duration: 5000,
      closable: true,
    });
  }, [flushAutoSave, setCloudCurrentKey, setCloudCurrentFilename]);

  useEffect(() => {
    registerBindHandlers(bindCloudFile, unbindCloudFile);
  }, [registerBindHandlers, bindCloudFile, unbindCloudFile]);

  // Open a local disk file, detaching from the cloud binding first so the
  // import never overwrites the bound cloud drawing. Reuses the same loader as
  // the native "Open" action (works on all browsers via fileOpen's fallback).
  const handleOpenLocal = useCallback(async () => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      const { elements, appState, files } = await loadFromJSON(
        excalidrawAPI.getAppState(),
        excalidrawAPI.getSceneElementsIncludingDeleted(),
      );
      detachCloudFile();
      excalidrawAPI.updateScene({
        elements,
        appState,
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      if (files && Object.keys(files).length) {
        excalidrawAPI.addFiles(Object.values(files));
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        excalidrawAPI.setToast({ message: "Failed to open file." });
      }
    }
  }, [excalidrawAPI, detachCloudFile]);

  // While the Save dialog is open the user is choosing a destination — could
  // be the same key (overwrite) or a new one (Save As). Either way, letting
  // the pending autosave fire to the *previous* binding would corrupt the old
  // file with the current (potentially forked) scene. Cancel on dialog open;
  // it'll re-arm via the next onChange once the dialog closes.
  useEffect(() => {
    if (saveDialogOpen) {
      flushAutoSave.cancel();
    }
  }, [saveDialogOpen, flushAutoSave]);

  // beforeunload: best-effort flush. With a 30 s debounce, the in-flight PUT
  // will likely be terminated by the browser — the warning prompt at least
  // gives the user a chance to wait it out.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const dirty =
        currentBindingRef.current !== null &&
        (autoSaveStatus.kind === "pending" ||
          autoSaveStatus.kind === "saving" ||
          autoSaveStatus.kind === "error");
      if (dirty) {
        flushAutoSave.flush();
        e.preventDefault();
        e.returnValue = "Save in progress — leave anyway?";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [flushAutoSave, autoSaveStatus]);

  // Cloud save button title — surfaces autosave state
  const cloudSaveTitle = useMemo(() => {
    if (!cloudCurrentKey) {
      return "Save to Cloud";
    }
    const name = cloudCurrentKey.split("/").pop();
    switch (autoSaveStatus.kind) {
      case "pending":
      case "saving":
        return `Saving ${name}…`;
      case "saved":
        return `Saved — ${name}`;
      case "error":
        return `Save failed: ${autoSaveStatus.message} — click to retry (${name})`;
      case "conflict":
        return `Conflict — reopen ${name} to merge`;
      default:
        return `Synced with cloud: ${name}`;
    }
  }, [cloudCurrentKey, autoSaveStatus]);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          const statusColor = (() => {
            switch (autoSaveStatus.kind) {
              // "pending" and "saving" share one calm blue indicator. onChange
              // fires ~60×/s while drawing, so a distinct orange "Unsaved"
              // state flips back the instant any save lands — the chip would
              // read "Unsaved" ~99% of the time during active editing. Treat
              // "there are changes not yet confirmed durable" as "Saving…".
              case "pending":
              case "saving":
                return "#2680eb";
              case "error":
                return "#dc2626";
              case "conflict":
                return "#dc2626";
              case "saved":
              default:
                return "#1d9a4e";
            }
          })();
          const statusText = (() => {
            switch (autoSaveStatus.kind) {
              case "pending":
              case "saving":
                return "Saving…";
              case "saved":
                return "Saved";
              case "error":
                return "Save failed";
              case "conflict":
                return "Conflict";
              default:
                return "Synced";
            }
          })();

          return (
            <div className="excalidraw-ui-top-right">
              {cloudEnabled && cloudCurrentKey && (
                <div
                  title={cloudSaveTitle}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 10px",
                    borderRadius: 6,
                    background: "var(--island-bg-color, #fff)",
                    border: `1px solid ${statusColor}`,
                    fontSize: 13,
                    fontWeight: 500,
                    maxWidth: 280,
                    color: "var(--text-primary-color)",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: statusColor,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cloudCurrentKey
                      .split("/")
                      .pop()
                      ?.replace(".excalidraw", "")}
                  </span>
                  <span style={{ color: statusColor, fontSize: 11 }}>
                    {statusText}
                  </span>
                </div>
              )}
              {cloudEnabled && (
                <>
                  <button
                    className="excalidraw-button collab-button"
                    onClick={() => openOpenDialog()}
                    title="Open from Cloud"
                    style={{ width: "auto", gap: 8 }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    >
                      <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10z" />
                    </svg>
                    Open
                  </button>
                  <button
                    className="excalidraw-button collab-button"
                    onClick={() => openSaveDialog(undefined)}
                    title={cloudSaveTitle}
                    style={{ width: "auto", gap: 8 }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    >
                      <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
                    </svg>
                    {cloudCurrentKey ? "Save" : "Save to Cloud"}
                  </button>
                </>
              )}

              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          onOpenLocal={handleOpenLocal}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {openDialogOpen && excalidrawAPI && (
          <OpenFromCloudDialog
            excalidrawAPI={excalidrawAPI}
            onClose={closeOpenDialog}
            onOpened={async (key, filename, etag) => {
              // Bind BEFORE the dialog calls updateScene so the onChange that
              // fires from updateScene sees the new binding, not the old one.
              await bindCloudFile(key, etag);
              setCloudCurrentKey(key);
              setCloudCurrentFilename(filename.replace(".excalidraw", ""));
              closeOpenDialog();
              excalidrawAPI.setToast({
                message: `Opened: ${filename.replace(".excalidraw", "")}`,
                duration: 3000,
              });
            }}
          />
        )}

        {saveDialogOpen && excalidrawAPI && (
          <SaveToCloudDialog
            excalidrawAPI={excalidrawAPI}
            initialPrefix={saveDialogPrefix}
            onClose={closeSaveDialog}
            onSaved={async (key, filename, etag) => {
              // Save dialog already uploaded the current scene to `key`. Any
              // pending autosave for a *previous* binding would now write
              // current scene to the OLD key (corrupting the previous file
              // with the forked content). Cancel rather than flush.
              await bindCloudFile(key, etag, { flushPrevious: false });
              setCloudCurrentKey(key);
              setCloudCurrentFilename(filename);
              closeSaveDialog();
              excalidrawAPI.setToast({
                message: `Saved to cloud: ${filename}`,
                duration: 3000,
              });
            }}
          />
        )}

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawAppInner = () => {
  const { user, isLoading } = useCloud();
  const cloudGateEnabled = isCloudEnabled();

  if (cloudGateEnabled && isLoading) {
    // Brief loading state while restoring session
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontSize: "1rem",
          color: "#888",
        }}
      >
        Loading…
      </div>
    );
  }

  if (cloudGateEnabled && !user) {
    return <LoginScreen />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <CloudProvider>
      <ExcalidrawAppInner />
    </CloudProvider>
  );
};

export default ExcalidrawApp;
